<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h6 class="mb-0">Cotizaciones</h6>
            </div>
            <div class="row">
              <div class="col-md-4 mb-3">
                <label for="searchQuery" class="mb-1">Buscar</label>
                <input type="text" v-model="searchQuery" id="searchQuery" class="form-control"
                  placeholder="Nombre, teléfono o documento..." @input="onSearchInput" />
              </div>
              <div class="col-md-3 mb-3">
                <label for="statusFilter" class="mb-1">Estado</label>
                <select id="statusFilter" class="form-select" v-model="statusFilter" @change="fetchData(1)">
                  <option value="">Todas</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="convertida">Convertida</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>
            </div>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">#</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cliente</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Vendedor</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Entrega</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Fecha</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in cotizaciones" :key="item.id">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <h6 class="mb-0 text-sm">#{{ item.id }}</h6>
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ item.name }}</h6>
                          <p class="text-xs text-secondary mb-0">{{ item.phone }}</p>
                        </div>
                      </div>
                    </td>
                    <td class="align-middle text-center text-sm">
                      {{ item.seller?.name || '—' }}
                    </td>
                    <td class="align-middle text-center text-sm">
                      {{ item.delivery_type === 'domicilio' ? 'Domicilio' : 'Para llevar' }}
                    </td>
                    <td class="align-middle text-center text-sm font-weight-bold">
                      {{ formatCurrency(item.total) }}
                    </td>
                    <td class="align-middle text-center text-sm">
                      {{ formatDate(item.created_at) }}
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge badge-sm"
                        :class="{
                          'bg-gradient-warning': item.status === 'pendiente',
                          'bg-gradient-success': item.status === 'convertida' && item.type !== 'traslado',
                          'bg-gradient-info': item.status === 'convertida' && item.type === 'traslado',
                          'bg-gradient-secondary': item.status === 'cancelada',
                        }">
                        {{ statusLabel(item) }}
                      </span>
                      <p v-if="item.remission_id" class="text-xs text-secondary mb-0 mt-1">
                        Remisión #{{ item.remission_id }}
                        <span v-if="item.remission?.tipo_doc_id" class="badge badge-sm bg-gradient-secondary ms-1">
                          {{ item.remission.tipo_doc_id }}
                        </span>
                      </p>
                    </td>
                    <td class="align-middle text-center">
                      <button type="button" class="btn btn-link text-info px-3 mb-0"
                        data-toggle="tooltip" data-original-title="Ver detalle"
                        @click="openDetails(item)">
                        <i class="fas fa-eye"></i>
                      </button>
                      <button v-if="item.status !== 'convertida'" type="button"
                        class="btn btn-link text-danger px-3 mb-0 ms-4"
                        data-toggle="tooltip" data-original-title="Eliminar"
                        @click="removeCotizacion(item)">
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="cotizaciones.length === 0">
                    <td colspan="8" class="text-center text-xxs py-4">
                      {{ loading ? 'Cargando...' : 'No hay cotizaciones' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="pagination p-3" v-if="links.length > 3">
              <a v-for="link in links" :key="link.label" class="page-item cursor-pointer"
                :class="{ active: link.active }" @click="goToPage(link)">
                {{ cleanLabel(link.label) }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cotizacionService from "@/services/cotizacion.service";
import Swal from "sweetalert2";

export default {
  name: "Cotizaciones Table",
  data() {
    return {
      cotizaciones: [],
      links: [],
      searchQuery: "",
      statusFilter: "",
      loading: false,
      searchTimer: null,
    };
  },
  methods: {
    async fetchData(page = 1) {
      this.loading = true;
      try {
        const res = await cotizacionService.getAll({
          page,
          per_page: 15,
          search: this.searchQuery,
          status: this.statusFilter,
        });
        const data = res.data || {};
        this.cotizaciones = data.data || [];
        this.links = data.links || [];
      } catch (error) {
        console.error("Error al cargar cotizaciones:", error);
        Swal.fire("Error", "No se pudieron cargar las cotizaciones.", "error");
      } finally {
        this.loading = false;
      }
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchData(1), 400);
    },
    goToPage(link) {
      if (!link.url) return;
      const page = new URL(link.url).searchParams.get("page");
      this.fetchData(page || 1);
    },
    cleanLabel(label) {
      return String(label || "")
        .replace("&laquo;", "«")
        .replace("&raquo;", "»")
        .replace("Previous", "«")
        .replace("Next", "»");
    },
    formatCurrency(value) {
      return (Number(value) || 0).toLocaleString("es-CO", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString("es-CO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    statusLabel(item) {
      if (item.status === 'convertida') {
        return item.type === 'traslado' ? 'Convertida → Traslado' : 'Convertida → Venta';
      }
      const labels = {
        pendiente: "Pendiente",
        cancelada: "Cancelada",
      };
      return labels[item.status] || item.status;
    },
    openDetails(item) {
      this.$router.push({ name: "Cotizaciones Detail", query: { id: String(item.id) } });
    },
    async removeCotizacion(item) {
      const confirm = await Swal.fire({
        icon: "warning",
        title: "Eliminar cotización",
        text: `¿Eliminar la cotización #${item.id}?`,
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return;

      try {
        await cotizacionService.remove(item.id);
        await this.fetchData(1);
      } catch (error) {
        const msg = error?.response?.data?.message || "No se pudo eliminar la cotización.";
        Swal.fire("Error", msg, "error");
      }
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
